import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './TicketDetails.module.css';
import DataFallback from '../components/feedback/DataFallback';

const TicketDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [reopenModal, setReopenModal] = useState(false);
  const [reopenContext, setReopenContext] = useState('');
  const [reopenLoading, setReopenLoading] = useState(false);
  const [hasEscalated, setHasEscalated] = useState(false);
  const [showBookCallModal, setShowBookCallModal] = useState(false);
  const messagesEndRef = useRef(null);

  // ── Initial full ticket load ─────────────────────────────────────────────
  const fetchTicket = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/${ticketId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!res.ok) {
        setError(res.status === 404 ? 'Ticket not found' : 'Failed to load ticket details');
        return;
      }
      const data = await res.json();
      setTicket(data);
      setError(null);
    } catch {
      setError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  // ── Silent refetch — never touches loading state ─────────────────────────
  const silentRefetch = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/${ticketId}/`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setTicket(data);
    } catch { /* ignore */ }
  }, [ticketId]);

  // ── 3-second poll — picks up admin replies ───────────────────────────────
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/${ticketId}/`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        const incoming = data.messages || [];
        setTicket(prev => {
          if (!prev) return data;
          // Only count real (non-optimistic) message IDs from prev
          const prevRealIds = new Set(
            (prev.messages || [])
              .filter(m => !String(m.id).startsWith('optimistic-'))
              .map(m => String(m.id))
          );
          const hasNew = incoming.some(m => !prevRealIds.has(String(m.id)));
          if (hasNew || data.status !== prev.status || data.urgency !== prev.urgency) {
            return { ...prev, messages: incoming, status: data.status, urgency: data.urgency };
          }
          return prev;
        });
      } catch { /* network hiccup */ }
    };

    const interval = setInterval(poll, 3000);
    return () => {
      clearInterval(interval);
    };
  }, [ticketId]);

  // Scroll to bottom whenever a new message arrives
  const messages = ticket?.messages || [];
  const msgCount = messages.length;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgCount]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatDate = (d) =>
    new Date(d).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    }) + ' IST';

  const statusClass = (s) => {
    switch (s) {
      case 'open': return styles.badgeOpen;
      case 'in_progress': return styles.badgeInProgress;
      case 'resolved': return styles.badgeResolved;
      case 'closed': return styles.badgeClosed;
      default: return '';
    }
  };

  const urgencyClass = (u) => {
    switch (u) {
      case 'low': return styles.badgeLow;
      case 'medium': return styles.badgeMedium;
      case 'high': return styles.badgeHigh;
      case 'critical': return styles.badgeCritical;
      default: return '';
    }
  };

  const fileIcon = (name) => {
    const ext = name?.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg'].includes(ext)) return '🖼️';
    if (ext === 'pdf') return '📄';
    if (['xlsx', 'xls'].includes(ext)) return '📊';
    if (['docx', 'doc'].includes(ext)) return '📝';
    return '📎';
  };

  // ── Reopen handler ────────────────────────────────────────────────────────
  const handleReopenSubmit = async () => {
    if (!reopenContext.trim()) return;
    setReopenLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/${ticketId}/reopen/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ context: reopenContext }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        alert(err?.error?.message || 'Reopen failed.');
        return;
      }
      setReopenModal(false);
      setReopenContext('');
      // Reload ticket — status is now 'open', chat thread has new system message
      await silentRefetch();
    } catch {
      alert('Reopen request failed. Please try again.');
    } finally {
      setReopenLoading(false);
    }
  };

  // ── Escalate ──────────────────────────────────────────────────────────────
  const handleEscalate = async () => {
    setHasEscalated(true); // hide button immediately
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/${ticketId}/escalate/`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      if (!res.ok) {
        const err = await res.json();
        setHasEscalated(false); // restore button on failure
        alert(err?.error?.message || 'Escalation failed.');
        return;
      }
      await silentRefetch();
    } catch {
      setHasEscalated(false); // restore button on failure
      alert('Escalation request failed. Please try again.');
    }
  };

  // ── Reply send ────────────────────────────────────────────────────────────
  const handleReplySubmit = async (e) => {
    e.preventDefault();
    const text = replyText.trim();
    if (!text) return;

    // Clear box immediately
    setReplyText('');
    setSendingReply(true);

    // Optimistic insert
    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticMsg = {
      id: optimisticId,
      sender_type: 'brand',
      sender_name: 'You',
      content: text,
      created_at: new Date().toISOString(),
      attachments: [],
    };
    setTicket(prev => prev ? { ...prev, messages: [...(prev.messages || []), optimisticMsg] } : prev);

    try {
      const payload = new FormData();
      payload.append('content', text);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/${ticketId}/messages/`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: payload,
        }
      );
      if (!res.ok) throw new Error('Failed to send reply');
      await silentRefetch();
    } catch {
      // Roll back and restore text
      setTicket(prev =>
        prev ? { ...prev, messages: (prev.messages || []).filter(m => m.id !== optimisticId) } : prev
      );
      setReplyText(text);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  // ── States────
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingScreen}>
          <div className={styles.loadingDots}><span/><span/><span/></div>
          <p>Loading ticket…</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    // A ticket that genuinely does not exist and a ticket we could not reach are
    // different situations: the first is final, the second clears on its own.
    return (
      <div className={styles.page}>
        <DataFallback
          variant="pane"
          error={error || { kind: 'notFound', retryable: false }}
          onRetry={error ? () => window.location.reload() : undefined}
        >
          <button className={styles.backBtn} onClick={() => navigate('/suspire-helpdesk')}>
            ← Back to Tickets
          </button>
        </DataFallback>
      </div>
    );
  }

  const canReply = ticket.status !== 'closed';
  const canReopen = ticket.status === 'resolved' || ticket.status === 'closed';

  return (
    <div className={styles.page}>

      {/* ── Book-a-call modal ────────────────────────────────────────────── */}
      {showBookCallModal && (
        <div className={styles.modalOverlay} onClick={() => setShowBookCallModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>📅 Need deeper assistance?</h3>
            <p className={styles.modalBody}>
              Please book a call only if your issue requires a detailed discussion.
              For most queries, our team will resolve it over chat.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.modalPrimaryBtn}
                onClick={() => {
                  const url = ticket?.category_detail?.calendly_url;
                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                  setShowBookCallModal(false);
                }}
              >
                Confirm & Open Calendar
              </button>
              <button className={styles.modalSecondaryBtn} onClick={() => setShowBookCallModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Reopen modal ────────────────────────────────────────────────── */}
      {reopenModal && (
        <div className={styles.modalOverlay} onClick={() => setReopenModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>🔓 Reopen Ticket</h3>
            <p className={styles.modalBody}>
              Please share why you need to reopen this ticket. Your reason will be added to the conversation thread and the support team will be notified.
            </p>
            <textarea
              className={styles.modalTextarea}
              placeholder="Describe why you need to reopen this ticket…"
              value={reopenContext}
              onChange={e => setReopenContext(e.target.value)}
              rows={4}
              maxLength={500}
              autoFocus
            />
            <div className={styles.modalActions}>
              <button
                className={styles.modalPrimaryBtn}
                onClick={handleReopenSubmit}
                disabled={!reopenContext.trim() || reopenLoading}
              >
                {reopenLoading ? 'Submitting…' : 'Reopen Ticket'}
              </button>
              <button
                className={styles.modalSecondaryBtn}
                onClick={() => { setReopenModal(false); setReopenContext(''); }}
                disabled={reopenLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Sticky header ───────────────────────────────────────────────── */}
      <div className={styles.chatHeader}>
        <button className={styles.backBtn} onClick={() => navigate('/suspire-helpdesk')}>
          ← Back
        </button>
        <div className={styles.headerMeta}>
          <span className={styles.headerTicketId}>{ticket.display_id}</span>
          <span className={styles.headerTitle}>{ticket.title}</span>
        </div>
      </div>

      {/* ── Ticket description banner ────────────────────────────────────── */}
      <div className={styles.descriptionBanner}>
        {/* Ticket actions row */}
        <div className={styles.ticketActions}>
          {ticket.is_pinned && (
            <span
              className={styles.pinnedBadge}
              title="Your permanent main thread with Suspire — always pinned to the top"
            >
              📌 Pinned · Main thread
            </span>
          )}
          <span className={`${styles.badge} ${statusClass(ticket.status)}`}>
            {ticket.status.replace('_', ' ')}
          </span>
          <span className={`${styles.badge} ${urgencyClass(ticket.urgency)}`}>
            {ticket.urgency}
          </span>
          {/* The pinned main thread is never escalated. */}
          {!ticket.is_pinned && (ticket.escalated || hasEscalated ? (
            <span className={styles.escalatedBadge} title={ticket.escalated_at ? `Escalated on ${formatDate(ticket.escalated_at)}` : 'Already escalated'}>
              Escalated ✓
            </span>
          ) : ticket.status !== 'closed' ? (
            <span title={!ticket.can_escalate ? 'Escalation available after 48 hours of ticket creation' : 'Escalate this ticket'}>
              <button
                className={styles.escalateBtn}
                onClick={handleEscalate}
                disabled={!ticket.can_escalate}
              >
                ⬆ Escalate
              </button>
            </span>
          ) : null)}
          {ticket.category_detail?.calendly_url && (
            <button
              className={styles.bookCallHeaderBtn}
              onClick={() => setShowBookCallModal(true)}
            >
              📅 Need deeper assistance? Book a call
            </button>
          )}
        </div>

        <div className={styles.descriptionMeta}>
          <span>📅 Opened {formatDate(ticket.created_at)}</span>
          {(ticket.category_detail?.name || ticket.category_name) && (
            <span>📂 {ticket.category_detail?.name || ticket.category_name}</span>
          )}
          {ticket.last_activity_at && <span>🕑 Last activity {formatDate(ticket.last_activity_at)}</span>}
        </div>
        <div className={styles.descriptionText}>{ticket.description}</div>
        {/* Ticket-level attachments (files attached at ticket creation) */}
        {ticket.attachments?.length > 0 && (
          <div className={styles.attachmentList}>
            {ticket.attachments.map(a => (
              <a
                key={a.id}
                href={a.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.attachmentChip}
              >
                {fileIcon(a.file_name)} {a.file_name}
              </a>
            ))}
          </div>
        )}
      </div>

      {/* ── Chat thread ─────────────────────────────────────────────────── */}
      <div className={styles.chatThread}>
        {/* Avg response time banner — hidden once admin has replied */}
        {!messages.some(m => m.sender_type === 'admin') && (
          <div className={styles.avgResponseBanner}>
            <span className={styles.avgResponseIcon}>⏱</span>
            <span>Avg. response time is <strong>24 hours</strong>. We'll notify you by email when we reply.</span>
          </div>
        )}

        {messages.length === 0 && (
          <div className={styles.noMessages}>No replies yet — our team will respond shortly.</div>
        )}

        {messages.map(msg => {
          const isAdmin = msg.sender_type === 'admin';
          return (
            <div key={msg.id} className={`${styles.msgRow} ${isAdmin ? styles.fromAdmin : styles.fromUser}`}>
              <div className={`${styles.avatar} ${isAdmin ? styles.avatarAdmin : styles.avatarUser}`}>
                {isAdmin ? 'S' : (msg.sender_name?.charAt(0)?.toUpperCase() || 'B')}
              </div>
              <div className={styles.bubble}>
                <div className={styles.bubbleMeta}>
                  <span className={styles.bubbleSender}>
                    {isAdmin ? 'Suspire Support' : 'You'}
                  </span>
                  <span className={styles.bubbleTime}>{formatDate(msg.created_at)}</span>
                </div>
                <div className={styles.bubbleContent}>{msg.content}</div>
                {msg.attachments?.length > 0 && (
                  <div className={styles.attachmentList}>
                    {msg.attachments.map(a => (
                      <a
                        key={a.id}
                        href={a.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.attachmentChip}
                      >
                        {fileIcon(a.file_name)} {a.file_name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Reply box / Reopen bar ──────────────────────────────────────── */}
      {canReopen ? (
        <div className={styles.closedBanner}>
          <div className={styles.closedBannerText}>
            {ticket.status === 'resolved'
              ? '✅ This ticket has been resolved.'
              : '🔒 This ticket is closed.'}
            {' '}If you still need help, reopen it and our team will continue from where we left off.
          </div>
          <button
            className={styles.reopenBannerBtn}
            onClick={() => setReopenModal(true)}
          >
            🔓 Reopen Ticket
          </button>
        </div>
      ) : canReply ? (
        <form className={styles.replyBox} onSubmit={handleReplySubmit}>
          <textarea
            className={styles.replyTextarea}
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="Type your reply… (Enter to send, Shift+Enter for new line)"
            rows={3}
            maxLength={2000}
            disabled={sendingReply}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (replyText.trim() && !sendingReply) {
                  handleReplySubmit(e);
                }
              }
            }}
          />
          <div className={styles.replyActions}>
            <button
              type="submit"
              className={styles.sendBtn}
              disabled={sendingReply || !replyText.trim()}
            >
              {sendingReply ? <span className={styles.spinner} /> : '➤ Send'}
            </button>
            <span className={styles.charCount}>{replyText.length}/2000</span>
          </div>
        </form>
      ) : (
        <div className={styles.closedBanner}>
          🔒 This ticket is in an unrecognised state.
        </div>
      )}
    </div>
  );
};

export default TicketDetails;
